import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AddCrossTinyIcon,
  Body2,
  Button,
  Dialog,
  IconButton,
  InputNumber,
  Label,
  MinusIcon,
  Select,
  SelectTrigger,
} from "@activecollab/components";
import PropTypes from "prop-types";

export const VariableDialog = ({ onClose, onSubmit, open, options, ...rest }) => {
  const { t } = useTranslation();
  const [selectedVariableId, setSelectedVariableId] = useState(options[0].id);
  const [offset, setOffset] = useState(0);

  const selectedVariable = useMemo(
    () => options.find(({ id }) => id === selectedVariableId),
    [options, selectedVariableId]
  );

  const handleSubmit = useCallback(() => {
    const submitFormat =
      offset === 0
        ? `{${selectedVariableId}}`
        : offset > 0
        ? `{${selectedVariableId}+${offset}}`
        : `{${selectedVariableId}${offset}}`;
    onSubmit?.(submitFormat);
    onClose?.();
  }, [offset, selectedVariableId, onSubmit, onClose]);

  return (
    <Dialog
      {...rest}
      open={open}
      onClose={onClose}
    >
      <Dialog.Title>{t("Insert Variable")}</Dialog.Title>
      <Dialog.ContentDivider />
      <Dialog.Content>
        <>
          <div className="tw-flex tw-items-center tw-mb-4 tw-gap-3">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <Label
                size="small"
                htmlFor="variable"
              >
                {t("Variable")}
              </Label>
              <Select
                target={<SelectTrigger className="tw-w-[200px] tw-capitalize">{selectedVariable?.name}</SelectTrigger>}
                options={options}
                focusInput
                selected={selectedVariableId}
                name="variable"
                onChange={(val) => {
                  setSelectedVariableId(val);
                  setOffset(0);
                }}
                noResultText={t("No Result")}
                placeholder={t("Search a variable")}
                keepSameOptionsOrder
                disabledInternalSort
                forceCloseMenu
                id="variable"
              />
            </div>
            {selectedVariable?.canBeModified ? (
              <div className="tw-flex tw-flex-col tw-gap-1">
                <Label
                  size="small"
                  htmlFor="offset"
                >
                  {t("Offset")}
                </Label>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <IconButton
                    variant="option"
                    onClick={(e) => {
                      e.preventDefault();
                      setOffset((prev) => prev - 1);
                    }}
                  >
                    <MinusIcon />
                  </IconButton>
                  <InputNumber
                    name="offset"
                    value={offset}
                    onChange={(val) => setOffset(val)}
                    id="offset"
                    className="tw-w-12"
                    decimalLength={0}
                  />
                  <IconButton
                    variant="option"
                    onClick={(e) => {
                      e.preventDefault();
                      setOffset((prev) => prev + 1);
                    }}
                  >
                    <AddCrossTinyIcon />
                  </IconButton>
                </div>
              </div>
            ) : null}
          </div>
          {selectedVariable?.canBeModified ? (
            <Body2 color="secondary">{t("Offset numeric variables by adding or subtracting whole numbers.")}</Body2>
          ) : null}
        </>
      </Dialog.Content>
      <Dialog.ContentDivider />
      <Dialog.Actions>
        <Button
          type="submit"
          className="tw-mr-3"
          onClick={handleSubmit}
        >
          {t("Insert")}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
        >
          {t("Cancel")}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

VariableDialog.propTypes = {
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  open: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      canBeModified: PropTypes.bool,
    }).isRequired
  ),
};

VariableDialog.defaultProps = {
  options: [],
};
